import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const ramatuelleEn: ChauffeurPage = {
  slug: "ramatuelle",
  title: "VTC & private chauffeur in Ramatuelle",
  subtitle:
    "Private chauffeur in Ramatuelle: Nice airport transfers (~1 h 30), Pampelonne beach drop-offs, hilltop village and vineyards, on the Saint-Tropez peninsula, 7 days a week.",
  keywords: [
    "vtc ramatuelle",
    "private chauffeur ramatuelle",
    "pampelonne beach chauffeur",
    "ramatuelle nice airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Ramatuelle",
    subtitle:
      "From Pampelonne beach to the hilltop village, on the Saint-Tropez peninsula: Nice airport transfers (~1 h 30) and beach-club drop-offs, 24/7.",
    keywords: [
      "VTC Ramatuelle",
      "Pampelonne chauffeur",
      "Pampelonne beach club",
      "Nice airport transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "Private chauffeur & VTC in Ramatuelle, Pampelonne beach and hilltop village",
    badges: [
      "Nice airport ~1 h 30",
      "Pampelonne beach drop-off",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Ramatuelle",
    items: [
      {
        title: "Drop-off at the Pampelonne beach clubs",
        description:
          "Narrow road and saturated car parks in summer: the chauffeur drops you at the entrance to the Pampelonne beach clubs and returns on call.",
      },
      {
        title: "Nice airport in ~1 h 30",
        description:
          "The Saint-Tropez peninsula is the farthest point: we allow a wide margin and track your flight for this long transfer.",
      },
      {
        title: "Hilltop village, no parking",
        description:
          "The medieval village's circular lanes are pedestrian: drop-off at the foot of the ramparts, pick-up at the same spot.",
      },
      {
        title: "Vineyards and peninsula",
        description:
          "Côtes de Provence estates, Cap Camarat, l'Escalet: bespoke itineraries, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Ramatuelle",
    services: [
      {
        title: "Nice airport transfers",
        text: "Ramatuelle ⇄ Nice Côte d'Azur airport in ~1 h 30, with flight tracking and a wide anticipated margin.",
      },
      {
        title: "Pampelonne beach shuttle",
        text: "Drop-off and pick-up at the beach-club entrances, with no parking worry or saturated road.",
      },
      {
        title: "Hilltop village & Saint-Tropez",
        text: "A link between the medieval village, Saint-Tropez (minutes away) and the peninsula's coves.",
      },
      {
        title: "Vineyard route",
        text: "Côtes de Provence estates around Ramatuelle, tastings and dinners, dedicated chauffeur for the return.",
      },
      {
        title: "Saint-Raphaël TGV shuttle & long distance",
        text: "A link to Saint-Raphaël-Valescure station and journeys from Cannes, Nice or the airport.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Pampelonne beach",
        description:
          "Beach clubs and car parks, drop-off as close as possible to the entrance.",
      },
      {
        name: "Ramatuelle hilltop village",
        description: "Ramparts, circular lanes and the Théâtre de Verdure.",
      },
      {
        name: "Cap Camarat & lighthouse",
        description:
          "Tip of the peninsula and one of France's tallest lighthouses.",
      },
      {
        name: "L'Escalet",
        description: "Cove and start of the coastal path towards Pampelonne.",
      },
      {
        name: "Wine estates",
        description: "Côtes de Provence vineyards around the village.",
      },
      {
        name: "Nice airport",
        description: "Terminals 1 and 2, ~1 h 30 via the A8.",
      },
    ],
  },
  generic: {
    heading: "A chauffeur from Pampelonne to the hilltop village",
    subheading:
      "Ramatuelle is Pampelonne beach below and a medieval village balconied over the peninsula.",
    paragraphs: [
      "On the Saint-Tropez peninsula, Ramatuelle brings together the famous Pampelonne beach and its clubs, a perched medieval village with circular lanes and the Côtes de Provence vineyards. KS Rent Services handles your journeys here with a private chauffeur: club drop-offs, a link to Saint-Tropez and chauffeur hire, without ever facing the saturated summer car parks.",
      "Nice Côte d'Azur airport is around 1 h 30 away: the peninsula is the farthest point of our towns, so we allow a wide margin and track your flight. For the train, Saint-Raphaël TGV station serves as the transfer point at the mouth of the gulf.",
      "During the Ramatuelle Festival in August, as during Les Voiles de Saint-Tropez, traffic builds on the peninsula: the chauffeur adjusts the timing, favours drop-off as close as possible and returns on a single call, for Pampelonne as for the village.",
    ],
    bullets: [
      "Nice airport in ~1 h 30",
      "Drop-off at the Pampelonne beach clubs",
      "Hilltop village & vineyards",
    ],
  },
  process: {
    heading: "Book a chauffeur in Ramatuelle",
    steps: [
      "Tell us your route (airport, Pampelonne, village, Saint-Tropez) and your times.",
      "We confirm the vehicle, the wide margin for Nice airport and the fixed package.",
      "The chauffeur tracks your flight and greets you at the airport or the agreed address.",
      "Drop-off at the beach clubs or the village foot, pick-up on a single call.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Ramatuelle"),
    {
      q: "How long to Nice airport?",
      a: "Around 1 h 30. The Saint-Tropez peninsula is the farthest from Nice airport: we allow a wide margin and track your flight.",
    },
    {
      q: "Do you drop off at the Pampelonne beach clubs?",
      a: "Yes, it's the most requested service: we drop you at the club entrance and return on call, with no parking worry or saturated road.",
    },
    {
      q: "Do you operate during the Ramatuelle Festival?",
      a: "Yes, in August the village is very busy: we adjust timing and drop-off around the Théâtre de Verdure, as for Les Voiles de Saint-Tropez.",
    },
    {
      q: "Which is the nearest TGV station?",
      a: "Saint-Raphaël-Valescure, at the mouth of the gulf: we provide the shuttle for your TGV departures and arrivals.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in summer and during peninsula events; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes E-Class and S-Class saloons, 7-seat vans, SUVs and VIP minibuses, depending on passengers and luggage.",
    },
  ],
  relatedLinks: [
    {
      label: "Private chauffeur in Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    { label: "Chauffeur service Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Private chauffeur on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Car rental with delivery in Saint-Tropez",
      href: "/en/location/car-rental-saint-tropez",
    },
  ],
  cta: {
    title: "Your private chauffeur in Ramatuelle",
    text: "Nice airport transfer, Pampelonne drop-off or chauffeur hire on the peninsula: fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Ramatuelle — private chauffeur & transfers",
    description:
      "Private chauffeur in Ramatuelle: Nice airport transfers (~1 h 30), Pampelonne beach drop-offs, hilltop village and Saint-Tropez peninsula vineyards.",
    canonical: "/en/chauffeur/ramatuelle",
  },
};

export default ramatuelleEn;
